import { Box, Typography } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import React, { useImperativeHandle, useState } from "react";
import theme from "Theme/AppTheme";

export type EmissionsJsonFieldRef = {
    /** The reason the current value is not acceptable, or undefined when it is. */
    validate: () => string | undefined;
};

type Props = {
    id: string;
    label: string;
    headerLabel?: string;
    description?: string;
    required?: boolean;
    value: string;
    onChange: (value: string) => void;
    onValidityChange?: (isValid: boolean) => void;
    readonly?: boolean;
    rowsMin?: number;
    rowsMax?: number;
};

/**
 * Why a value is not acceptable, or undefined when it is. An empty required field and malformed
 * JSON fail for different reasons, and "Enter valid JSON." under an empty box names neither.
 */
export const getJsonTextProblem = (
    text: string,
    required: boolean,
    label: string,
) => {
    if (!text.trim()) return required ? `${label} is required.` : undefined;
    try {
        JSON.parse(text);
        return undefined;
    } catch {
        return `${label} must be valid JSON.`;
    }
};

export const toJsonText = (value: unknown) =>
    value === null || value === undefined ? "" : JSON.stringify(value, null, 2);

export const parseJsonText = (text: string): unknown =>
    text.trim() ? JSON.parse(text) : null;

const EmissionsJsonField = React.forwardRef<EmissionsJsonFieldRef, Props>(
    (
        {
            id,
            label,
            headerLabel = "JSON",
            description,
            required = false,
            value,
            onChange,
            onValidityChange,
            readonly,
            rowsMin = 4,
            rowsMax = 16,
        },
        ref,
    ) => {
        const [focused, setFocused] = useState(false);
        const [problem, setProblem] = useState<string>();
        const isInvalid = problem !== undefined;

        const runValidation = (text: string) => {
            const found = getJsonTextProblem(text, required, label);
            setProblem(found);
            onValidityChange?.(found === undefined);
            return found;
        };

        useImperativeHandle(ref, () => ({
            validate: () => runValidation(value),
        }));

        const borderColor = isInvalid
            ? theme.palette.error.main
            : focused
            ? theme.palette.primary.main
            : theme.palette.gray[200];

        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <Typography
                    component="label"
                    htmlFor={id}
                    sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: theme.palette.gray[700],
                    }}
                >
                    {label}
                    {required && (
                        <Box
                            component="span"
                            sx={{ color: theme.palette.error.main, ml: 0.5 }}
                        >
                            *
                        </Box>
                    )}
                </Typography>
                {description && (
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: theme.palette.gray.main,
                        }}
                    >
                        {description}
                    </Typography>
                )}
                <Box
                    sx={{
                        border: `1px solid ${borderColor}`,
                        boxShadow:
                            focused && !isInvalid
                                ? `0 0 0 1px ${theme.palette.primary.main}`
                                : "none",
                        borderRadius: "8px",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: theme.palette.gray[50],
                            borderBottom: `1px solid ${theme.palette.gray[200]}`,
                            padding: "8px 12px",
                            fontSize: "11px",
                            color: theme.palette.gray.main,
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>{headerLabel}</span>
                        <span>application/json</span>
                    </Box>
                    <AcxMainTextField
                        id={id}
                        value={value}
                        multiline
                        fullWidth
                        readonly={readonly}
                        rowsMin={rowsMin}
                        rowsMax={rowsMax}
                        onChange={(evt) => onChange(evt.target.value)}
                        onFocus={() => setFocused(true)}
                        onBlur={(text) => {
                            setFocused(false);
                            runValidation(String(text ?? ""));
                        }}
                        textContainerSxOverride={{
                            display: "flex",
                            backgroundColor: theme.palette.white.main,
                            padding: "4px 4px",
                        }}
                        sx={{
                            fontFamily: "monospace",
                            fontSize: "12px",
                            lineHeight: 1.75,
                        }}
                    />
                </Box>
                {problem && (
                    <Typography
                        sx={{
                            fontSize: "12px",
                            lineHeight: 1.5,
                            color: theme.palette.error.main,
                            px: "2px",
                        }}
                    >
                        {problem}
                    </Typography>
                )}
            </Box>
        );
    },
);

EmissionsJsonField.displayName = "EmissionsJsonField";

export default EmissionsJsonField;
